import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Moon, Plus, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { useState } from 'react';
import { SleepLog } from '@/hooks/useSleepTracking';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface SleepTrackingCardProps {
  sleepLogs: SleepLog[];
  weeklyAverage: number;
  trend: 'improving' | 'declining' | 'stable';
  onLogSleep: (sleep: Omit<SleepLog, 'id'>) => void;
}

export const SleepTrackingCard = ({ sleepLogs, weeklyAverage, trend, onLogSleep }: SleepTrackingCardProps) => {
  const [showLogDialog, setShowLogDialog] = useState(false);
  const [newSleepLog, setNewSleepLog] = useState({
    sleep_date: new Date().toISOString().split('T')[0],
    hours_slept: '8', // String to allow comma (8,5) or period (8.5)
    quality_rating: 5,
    notes: ''
  });

  const handleLogSleep = () => {
    const hours = parseFloat(String(newSleepLog.hours_slept).replace(',', '.')) || 0;
    onLogSleep({
      ...newSleepLog,
      hours_slept: hours,
    });
    setNewSleepLog({
      sleep_date: new Date().toISOString().split('T')[0],
      hours_slept: '8',
      quality_rating: 5,
      notes: ''
    });
    setShowLogDialog(false);
  };

  const getTrendIcon = () => {
    switch (trend) {
      case 'improving':
        return <TrendingUp className="h-4 w-4 text-green-500" />;
      case 'declining':
        return <TrendingDown className="h-4 w-4 text-red-500" />;
      default:
        return <Minus className="h-4 w-4 text-yellow-500" />;
    }
  };

  const getTrendColor = () => {
    switch (trend) {
      case 'improving':
        return 'text-green-500';
      case 'declining':
        return 'text-red-500';
      default:
        return 'text-yellow-500';
    }
  };

  // Determine if we should show 7 or 30 days based on available data
  const showExtendedView = sleepLogs.length > 7;
  const daysToShow = showExtendedView ? 30 : 7;
  const periodLabel = showExtendedView ? '30-Day' : '7-Day';
  const chartLabel = showExtendedView ? 'Last 30 Days' : 'Last 7 Days';
  
  // Prepare chart data
  const chartData = sleepLogs.slice(0, daysToShow).reverse().map(log => ({
    date: new Date(log.sleep_date).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    }),
    hours: log.hours_slept
  }));

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Moon className="h-5 w-5 text-primary" />
            Sleep Tracking
          </CardTitle>
          <Dialog open={showLogDialog} onOpenChange={setShowLogDialog}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline">
                <Plus className="h-4 w-4 mr-2" />
                Log Sleep
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Log Sleep</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="sleepDate">Date</Label>
                  <Input
                    id="sleepDate"
                    type="date"
                    value={newSleepLog.sleep_date}
                    onChange={(e) => setNewSleepLog({...newSleepLog, sleep_date: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="hoursSlept">Hours Slept</Label>
                  <Input
                    id="hoursSlept"
                    type="text"
                    inputMode="decimal"
                    placeholder="e.g. 8.5 or 8,5"
                    value={newSleepLog.hours_slept}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '' || /^[0-9]*[,.]?[0-9]*$/.test(v)) {
                        setNewSleepLog({...newSleepLog, hours_slept: v});
                      }
                    }}
                    onFocus={(e) => e.target.select()}
                  />
                </div>
                <div>
                  <Label htmlFor="quality">Quality Rating (1-5)</Label>
                  <Input
                    id="quality"
                    type="number"
                    min="1"
                    max="5"
                    value={newSleepLog.quality_rating || ''}
                    onChange={(e) => setNewSleepLog({...newSleepLog, quality_rating: parseInt(e.target.value) || 5})}
                    onFocus={(e) => e.target.select()}
                  />
                </div>
                <div>
                  <Label htmlFor="notes">Notes (Optional)</Label>
                  <Input
                    id="notes"
                    value={newSleepLog.notes}
                    onChange={(e) => setNewSleepLog({...newSleepLog, notes: e.target.value})}
                    placeholder="How did you sleep?"
                  />
                </div>
                <Button onClick={handleLogSleep} className="w-full">
                  Log Sleep
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Sleep Stats */}
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold text-primary">{weeklyAverage}h</div>
              <p className="text-sm text-muted-foreground">{periodLabel} Average</p>
            </div>
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <div className={`flex items-center justify-center gap-2 ${getTrendColor()}`}>
                {getTrendIcon()}
                <span className="font-semibold capitalize">{trend}</span>
              </div>
              <p className="text-sm text-muted-foreground">Trend</p>
            </div>
          </div>

          {/* Sleep Quality Indicator */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Sleep Quality</span>
              <span className="text-sm text-muted-foreground">
                {weeklyAverage >= 8 ? 'Excellent' : weeklyAverage >= 7 ? 'Good' : weeklyAverage >= 6 ? 'Fair' : 'Poor'}
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all duration-300 ${
                  weeklyAverage >= 8 ? 'bg-green-500' : 
                  weeklyAverage >= 7 ? 'bg-yellow-500' : 
                  'bg-red-500'
                }`}
                style={{ 
                  width: `${Math.min((weeklyAverage / 8) * 100, 100)}%`,
                  filter: `drop-shadow(0 0 8px ${
                    weeklyAverage >= 8 ? '#22c55e' : 
                    weeklyAverage >= 7 ? '#eab308' : 
                    '#ef4444'
                  }80) drop-shadow(0 0 4px ${
                    weeklyAverage >= 8 ? '#22c55e' : 
                    weeklyAverage >= 7 ? '#eab308' : 
                    '#ef4444'
                  })`
                }}
              />
            </div>
          </div>

          {/* Sleep Chart */}
          {chartData.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium">{chartLabel}</h4>
              <div className="h-32">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis 
                      dataKey="date" 
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis 
                      domain={[0, 12]}
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip 
                      content={({ active, payload, label }) => {
                        if (active && payload && payload.length) {
                          return (
                            <div className="bg-background p-2 border rounded shadow-sm">
                              <p className="text-sm">{label}</p>
                              <p className="text-sm font-medium text-primary">
                                {payload[0].value}h sleep
                              </p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="hours" 
                      stroke="hsl(var(--primary))" 
                      strokeWidth={2}
                      dot={false}
                      activeDot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {sleepLogs.length === 0 && (
            <div className="text-center py-8">
              <Moon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-2">No sleep data yet</p>
              <p className="text-sm text-muted-foreground">
                Start logging your sleep to track patterns and improve recovery
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};